import { useRef } from "react";
import { Send, Paperclip, X } from "lucide-react";
import { ContactForm } from "./ContactForm";
import { MessageBubble } from "./MessageBubble";
import { chatWidgetCopy } from "@/lib/chat-widget-copy";

interface LiveChatBodyProps {
  messages: any[];
  threadRef: React.RefObject<HTMLDivElement | null>;
  agentTyping: boolean;
  hasMoreHistory: boolean;
  historyLoading: boolean;
  onLoadOlder: () => void;
  showContactForm: boolean;
  config: any;
  formValues: Record<string, string>;
  setFormValues: (v: Record<string, string>) => void;
  submitForm: (e: React.FormEvent) => void;
  submitting: boolean;
  formError: string | null;
  detectedCountry?: any;
  invalidPhoneField?: string;
  feedbackRequested: boolean;
  feedbackSubmitted: boolean;
  onFeedback: (f: "positive" | "negative") => void;
  onRestart: () => void;
  ended: boolean;
  endedReason?: string;
  text: string;
  setText: (t: string) => void;
  onSend: () => void;
  canSend: boolean;
  captchaRequired: boolean;
  attachments: any[];
  attachmentsConfig: any;
  onAttach: (f: File) => void;
  onRemoveAttachment: (id: string) => void;
  connection?: string;
  rateLimited?: boolean;
  errorMessage?: string;
}

export function LiveChatBody(props: LiveChatBodyProps) {
  const {
    messages,
    threadRef,
    agentTyping,
    hasMoreHistory,
    historyLoading,
    onLoadOlder,
    showContactForm,
    config,
    formValues,
    setFormValues,
    submitForm,
    submitting,
    formError,
    detectedCountry,
    invalidPhoneField,
    feedbackRequested,
    feedbackSubmitted,
    onFeedback,
    onRestart,
    ended,
    text,
    setText,
    onSend,
    canSend,
    captchaRequired,
    attachments,
    attachmentsConfig,
    onAttach,
    onRemoveAttachment,
    connection,
    rateLimited,
    errorMessage,
  } = props;

  const uploading = attachments.some((a: any) => a.status === "uploading");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canSendNow = canSend && !uploading && text.trim().length > 0;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div
        ref={threadRef}
        className="flex-1 min-h-0 overflow-y-auto space-y-3 p-4"
      >
        {hasMoreHistory && (
          <div className="flex justify-center pb-2">
            <button
              type="button"
              onClick={onLoadOlder}
              disabled={historyLoading}
              className="text-xs font-semibold text-[#D96B27] hover:underline disabled:opacity-50"
            >
              {historyLoading ? "Loading…" : "Load earlier messages"}
            </button>
          </div>
        )}
        {messages.map((m: any, i: number) => (
          <MessageBubble key={m.id ?? i} m={m} />
        ))}
        {agentTyping && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <span className="h-2 w-2 animate-pulse rounded-full bg-muted-foreground/60" />
            <span>The team is typing…</span>
          </div>
        )}
      </div>

      {(connection === "reconnecting" || connection === "offline") && (
        <div className="px-4 py-1 text-center text-[11px] text-muted-foreground">
          Reconnecting…
        </div>
      )}
      {rateLimited && !errorMessage && (
        <div className="px-4 py-1 text-center text-[11px] text-muted-foreground">
          You're sending messages a little too fast. Please wait a moment.
        </div>
      )}
      {errorMessage && (
        <div className="px-4 py-1 text-center text-[11px] text-destructive">
          {errorMessage}
        </div>
      )}

      {showContactForm && (
        <div className="border-t border-border p-3">
          <p className="mb-2 text-xs font-semibold">
            The team needs a few details:
          </p>
          <ContactForm
            config={config}
            formValues={formValues}
            setFormValues={setFormValues}
            submitForm={submitForm}
            submitting={submitting}
            formError={formError}
            detectedCountry={detectedCountry}
            invalidPhoneField={invalidPhoneField}
            compact
          />
        </div>
      )}

      {feedbackRequested && !feedbackSubmitted && (
        <div className="border-t border-border p-3">
          <p className="mb-2 text-center text-xs font-semibold">
            How did we do today?
          </p>
          <div className="flex justify-center gap-4">
            <button
              type="button"
              onClick={() => onFeedback("positive")}
              className="rounded-full border border-border px-4 py-2 text-sm hover:bg-muted"
            >
              👍
            </button>
            <button
              type="button"
              onClick={() => onFeedback("negative")}
              className="rounded-full border border-border px-4 py-2 text-sm hover:bg-muted"
            >
              👎
            </button>
          </div>
        </div>
      )}

      {feedbackSubmitted && (
        <div className="border-t border-border p-3 text-center">
          <p className="mb-2 text-xs text-muted-foreground">
            {config?.liveChat?.feedbackNote ?? "Thanks for your feedback."}
          </p>
          <button
            type="button"
            onClick={onRestart}
            className="text-xs font-semibold text-[#D96B27] hover:underline"
          >
            Start a new chat
          </button>
        </div>
      )}

      {ended && !feedbackRequested && (
        <div className="border-t border-border p-3 text-center">
          <button
            type="button"
            onClick={onRestart}
            className="text-xs font-semibold text-[#D96B27] hover:underline"
          >
            Restart chat
          </button>
        </div>
      )}

      {!showContactForm &&
        !feedbackRequested &&
        !feedbackSubmitted &&
        !ended && (
          <div className="border-t border-border p-3">
            {attachments.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-2">
                {attachments.map((a: any) => (
                  <span
                    key={a.id}
                    className="flex items-center gap-1 rounded bg-muted px-2 py-1 text-[11px]"
                  >
                    {a.name}
                    {a.status === "uploading" &&
                      ` (${Math.round((a.progress ?? 0) * 100)}%)`}
                    {a.status === "failed" && " — failed"}
                    <button
                      type="button"
                      onClick={() => onRemoveAttachment(a.id)}
                      className="text-muted-foreground hover:text-foreground"
                      aria-label="Remove attachment"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                onSend();
              }}
              className="flex items-end gap-2"
            >
              {attachmentsConfig?.enabled && (
                <>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    aria-label={chatWidgetCopy.attachAriaLabel}
                    className="rounded-lg border border-border p-2 text-muted-foreground hover:bg-muted"
                  >
                    <Paperclip className="h-4 w-4" />
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    accept={(attachmentsConfig?.acceptedTypes ?? []).join(",")}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) onAttach(f);
                      e.target.value = "";
                    }}
                  />
                </>
              )}
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Type a message…"
                rows={1}
                className="max-h-24 flex-1 resize-none rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
              <button
                type="submit"
                disabled={!canSendNow || captchaRequired}
                aria-label={chatWidgetCopy.sendAriaLabel}
                className="rounded-lg bg-[#D96B27] p-2.5 text-white hover:bg-[#BA541B] disabled:opacity-40"
              >
                <Send className="h-4 w-4" />
              </button>
            </form>
            {captchaRequired && (
              <p className="mt-1 text-[11px] text-muted-foreground">
                Please complete the captcha check above to send.
              </p>
            )}
          </div>
        )}
    </div>
  );
}
